import { useState, useEffect } from 'react';
import { api, ApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../components/ui/toast';

/**
 * CTO approved domains (epic 108 task 02, D2): the whole setting is ONE raw
 * comma-separated string — the textarea is bound to it verbatim and Save
 * replaces it whole. No per-domain rows, no client-side normalization or
 * validation display: parsing stays the read-side helper's job, so this
 * screen stays display-agnostic (the raw string is king).
 */
export function CtoApprovedDomains() {
  const [domains, setDomains] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { success } = useToast();

  useEffect(() => {
    api
      .getCtoApprovedDomains()
      .then((data) => setDomains(data.domains))
      .catch((err: ApiError) => setLoadError(err.message));
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);

    try {
      const data = await api.updateCtoApprovedDomains({ domains });
      setDomains(data.domains);
      success('Approved domains saved successfully');
    } catch (err) {
      const apiError = err as ApiError;
      const fieldErrors = apiError.validationErrors
        ? Object.values(apiError.validationErrors).flat().join(' ')
        : '';
      setSaveError([apiError.message, fieldErrors].filter(Boolean).join(': '));
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <div>
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">
          Error loading approved domains: {loadError}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Approved Domains</h1>
        <p className="text-muted-foreground mt-2">
          One comma-separated list of the domains allowed on CTO capture. Saved exactly as typed —
          spaces, case and duplicates are tolerated at capture time, so no formatting is applied
          here.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Approved Domains</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave}>
            <div className="space-y-2">
              <Label htmlFor="domains">Domains</Label>
              <Textarea
                id="domains"
                className="font-mono"
                rows={8}
                value={domains}
                onChange={(e) => setDomains(e.target.value)}
                placeholder="e.g., trading-tips.us, example.ai"
              />
              <p className="text-xs text-muted-foreground">
                Comma-separated. Stored and sent to capture exactly as saved.
              </p>
              {saveError && <p className="text-sm text-destructive">{saveError}</p>}
            </div>
            <div className="mt-4">
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
